import React, { useState } from "react";
import AdminTable from "../../Common/AdminTable";
import AdminModal from "../../Common/AdminModal";
import { recordAuditLog } from "../../../../services/adminService";
import { useAdminAuth } from "../../../../contexts/AdminAuthContext";
import { Bell, Send, Plus, CheckCircle2 } from "lucide-react";

const INITIAL_NOTIFICATIONS = [
  {
    id: "NOTIF-01",
    title: "Prelims 2024 Mini Mock 1 Live Now",
    message: "Candidate assessment test is now available. Log in to take the test.",
    audience: "All Users",
    recipients_count: 1420,
    sent_by: "operations@edukemy.com",
    sent_at: "2024-02-01T10:00:00Z",
    status: "Delivered",
  },
  {
    id: "NOTIF-02",
    title: "New Polity Current Affairs Dossier Available",
    message: "Monthly summary covering Supreme Court judgments and constitutional developments.",
    audience: "Premium Tier",
    recipients_count: 380,
    sent_by: "operations@edukemy.com",
    sent_at: "2024-02-10T14:30:00Z",
    status: "Delivered",
  },
];

export default function NotificationsPage() {
  const { adminUser } = useAdminAuth();
  const [notifications, setNotifications] = useState(INITIAL_NOTIFICATIONS);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [form, setForm] = useState({
    title: "",
    message: "",
    audience: "All Users",
  });

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 3500);
  };

  const handleSend = () => {
    if (!form.title.trim() || !form.message.trim()) {
      alert("Title and message are required");
      return;
    }

    const created = {
      id: `NOTIF-${Date.now()}`,
      title: form.title,
      message: form.message,
      audience: form.audience,
      recipients_count: form.audience === "All Users" ? 1500 : 420,
      sent_by: adminUser.email,
      sent_at: new Date().toISOString(),
      status: "Delivered",
    };

    setNotifications([created, ...notifications]);
    setIsModalOpen(false);

    recordAuditLog({
      action: "Notification Broadcast",
      module: "Notifications",
      entityType: "Notification",
      entityId: created.id,
      beforeValue: null,
      afterValue: created,
      adminUser: adminUser.email,
    });

    showToast(`Notification broadcasted to ${form.audience}`);
    setForm({ title: "", message: "", audience: "All Users" });
  };

  const columns = [
    {
      title: "Title & Message",
      key: "title",
      render: (val, row) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b" }}>{val}</div>
          <div style={{ fontSize: "12px", color: "#64748b" }}>{row.message}</div>
        </div>
      ),
    },
    {
      title: "Target Audience",
      key: "audience",
      width: "160px",
      render: (val) => (
        <span
          style={{
            fontSize: "12px",
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: "4px",
            background: "#eff6ff",
            color: "#2563eb",
          }}
        >
          {val}
        </span>
      ),
    },
    {
      title: "Recipients",
      key: "recipients_count",
      width: "120px",
      render: (val) => <span style={{ fontWeight: 600 }}>{val} users</span>,
    },
    {
      title: "Sent At",
      key: "sent_at",
      width: "160px",
      render: (val) => (
        <span style={{ fontSize: "12px", color: "#64748b" }}>
          {new Date(val).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: "100px",
      render: (val) => <span className="status-badge active">{val}</span>,
    },
  ];

  return (
    <div>
      {toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            background: "#0f172a",
            color: "#ffffff",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            zIndex: 2000,
            fontSize: "14px",
          }}
        >
          <CheckCircle2 size={18} style={{ color: "#22c55e" }} />
          {toastMessage}
        </div>
      )}

      <AdminTable
        columns={columns}
        data={notifications}
        searchPlaceholder="Search notifications..."
        actions={
          <button
            className="admin-btn admin-btn-primary"
            onClick={() => setIsModalOpen(true)}
          >
            <Plus size={15} /> Compose Notification
          </button>
        }
        pageSize={10}
        rowKey="id"
      />

      <AdminModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Compose & Send Notification"
        maxWidth="540px"
        footer={
          <>
            <button
              className="admin-btn admin-btn-secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="admin-btn admin-btn-primary"
              onClick={handleSend}
            >
              <Send size={14} /> Send Broadcast
            </button>
          </>
        }
      >
        <div className="admin-form-group">
          <label className="admin-form-label">Notification Title *</label>
          <input
            type="text"
            className="admin-form-input"
            placeholder="e.g. Weekly Current Affairs Test Scheduled"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">Target Audience *</label>
          <select
            className="admin-form-select"
            value={form.audience}
            onChange={(e) => setForm({ ...form, audience: e.target.value })}
          >
            <option value="All Users">All Registered Users</option>
            <option value="Free Tier">Free Tier Users Only</option>
            <option value="Premium Tier">Premium Subscribers Only</option>
            <option value="Prelims 2024 Batch">Prelims 2024 Target Batch</option>
          </select>
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">Message Body *</label>
          <textarea
            className="admin-form-textarea"
            rows={4}
            placeholder="Enter the notification announcement..."
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
        </div>
      </AdminModal>
    </div>
  );
}
